import { Box, Text } from '@mantine/core';
import { CSSProperties } from 'react';
import classes from './LongevityMeter.module.css';
import { OutcomeStatus, PlanScale, timelineLabel } from './modelOutcome';
import { deathsWithin, endingDeath, ModelTimeline, positionOnScale } from './timeline';

const STATUS_COLOUR: Record<Exclude<OutcomeStatus, 'notProjected'>, string> = {
  lasts: 'success',
  shortLate: 'warning',
  shortEarly: 'error',
};

// A label centres on its mark, except near either end of the bar, where it would overhang.
const markShift = (position: number): string => {
  if (position < 15) return 'none';
  if (position > 85) return 'translateX(-100%)';
  return 'translateX(-50%)';
};

type MeterProps = {
  status: Exclude<OutcomeStatus, 'notProjected'>;
  timeline: ModelTimeline;
  // The years the bar spans: the longest plan among the models it is read against, so a
  // plan that ends sooner stops short of the others.
  scale: number;
  size?: 'sm' | 'lg';
  // Names the life expectancy tick under the bar, where nothing else labels it.
  lifeExpectancyLabel?: string | null;
};

type Mark = { key: string; label: string; after: number };

const LIFE_EXPECTANCY = 'life-expectancy';

// Labels sit centred under their marks, so two close together would overlap: a label that
// would run into the one before it drops to the next line. Widths are judged from the text,
// at roughly two per cent of a row's bar per character.
const placeMarks = (marks: Mark[], scale: number) => {
  const lanes: { position: number; label: string }[] = [];
  return [...marks]
    .sort((a, b) => a.after - b.after)
    .map(mark => {
      const position = positionOnScale(scale, mark.after);
      const clear = (previous: { position: number; label: string }) =>
        position - previous.position > ((previous.label.length + mark.label.length) / 2) * 2 + 2;
      const free = lanes.findIndex(clear);
      const lane = free === -1 ? lanes.length : free;
      lanes[lane] = { position, label: mark.label };
      return { ...mark, position, lane };
    });
};

/**
 * The years a model's money lasts, from today to the end of the plan: solid while it
 * lasts, pale for the years with a shortfall, with a tick at the household's life
 * expectancy and a mark at each death the model records.
 */
export function LongevityMeter({
  status,
  timeline,
  scale,
  size = 'sm',
  lifeExpectancyLabel,
}: MeterProps) {
  const colour = STATUS_COLOUR[status];
  const { length, shortfallAfter, lifeExpectancyAfter } = timeline;
  const at = (after: number) => `${positionOnScale(scale, after)}%`;
  const deaths = deathsWithin(timeline);
  // Off the bar when it falls beyond the plans it is read against, or when the plan ends at a
  // recorded death; its label follows it.
  const showsLifeExpectancy =
    lifeExpectancyAfter != null && lifeExpectancyAfter <= scale && endingDeath(timeline) == null;
  const marks = placeMarks(
    [
      ...(showsLifeExpectancy && lifeExpectancyLabel
        ? [{ key: LIFE_EXPECTANCY, label: lifeExpectancyLabel, after: lifeExpectancyAfter }]
        : []),
      ...deaths.map(death => ({
        key: death.name,
        label: `${death.name} dies`,
        after: death.after,
      })),
    ],
    scale,
  );
  const markLanes = Math.max(0, ...marks.map(mark => mark.lane + 1));
  const style = {
    '--meter-bar': size === 'lg' ? '10px' : '8px',
    '--meter-fill': `var(--mantine-color-${colour}-6)`,
    '--meter-shortfall': `var(--mantine-color-${colour}-2)`,
  } as CSSProperties;

  return (
    <Box role="img" aria-label={timelineLabel(timeline)} data-testid="longevity-meter">
      <Box className={classes.meter} style={style} aria-hidden>
        <div className={classes.track} />
        {shortfallAfter != null && (
          <div
            className={classes.shortfall}
            style={{
              left: at(shortfallAfter),
              width: `calc(${at(length)} - ${at(shortfallAfter)})`,
            }}
          />
        )}
        <div
          className={classes.covered}
          data-cut={shortfallAfter != null || undefined}
          style={{ width: at(shortfallAfter ?? length) }}
        />
        {showsLifeExpectancy && (
          <div
            className={classes.lifeExpectancy}
            style={{ left: at(lifeExpectancyAfter) }}
            data-testid="life-expectancy-mark"
          />
        )}
        {shortfallAfter != null && (
          <div className={classes.runsOut} style={{ left: at(shortfallAfter) }} />
        )}
        {deaths.map(death => (
          <div key={death.name} className={classes.death} style={{ left: at(death.after) }} />
        ))}
      </Box>
      {marks.length > 0 && (
        <Box className={classes.marks} style={{ height: markLanes * 18 }} aria-hidden>
          {marks.map(mark => (
            // The life expectancy label reads like its label under a primary model's bar.
            <Text
              key={mark.key}
              size="xs"
              fw={mark.key === LIFE_EXPECTANCY ? 600 : undefined}
              c={mark.key === LIFE_EXPECTANCY ? 'gray.7' : 'dimmed'}
              className={classes.mark}
              style={{
                left: `${mark.position}%`,
                top: mark.lane * 18,
                transform: markShift(mark.position),
              }}
            >
              {mark.label}
            </Text>
          ))}
        </Box>
      )}
    </Box>
  );
}

/** The labels under a bar. The life expectancy label wins when it would crowd an end label. */
export function MeterScale({ labels, scale }: { labels: PlanScale; scale: number }) {
  const lifeExpectancyAt =
    labels.lifeExpectancy && positionOnScale(scale, labels.lifeExpectancy.after);
  const lines = Math.max(
    labels.start.length,
    labels.end.length,
    labels.lifeExpectancy?.lines.length ?? 0,
  );
  return (
    <Box className={classes.scale} style={{ height: lines * 18 }} data-testid="meter-scale">
      {!(lifeExpectancyAt != null && lifeExpectancyAt < 25) && (
        <ScaleLabel lines={labels.start} className={classes.scaleStart} />
      )}
      {labels.lifeExpectancy && (
        <ScaleLabel
          lines={labels.lifeExpectancy.lines}
          className={classes.scaleLifeExpectancy}
          left={`${lifeExpectancyAt}%`}
          strong
        />
      )}
      {!(lifeExpectancyAt != null && lifeExpectancyAt > 75) && (
        <ScaleLabel lines={labels.end} className={classes.scaleEnd} />
      )}
    </Box>
  );
}

function ScaleLabel({
  lines,
  className,
  left,
  strong = false,
}: {
  lines: string[];
  className: string;
  left?: string;
  strong?: boolean;
}) {
  const [first, ...rest] = lines;
  return (
    <Box className={className} style={left ? { left } : undefined}>
      <Text size="xs" fw={strong ? 600 : undefined} c={strong ? 'gray.7' : 'dimmed'}>
        {first}
      </Text>
      {rest.map(line => (
        <Text key={line} size="xs" c="dimmed">
          {line}
        </Text>
      ))}
    </Box>
  );
}
